// @ts-check
import { Text } from '~/design-system';
import { Trans } from '@lingui/macro';

export default function FieldError({
  error,
  bottlebooksFieldsMap,
  dataRowIndex = 2,
  columnLabel,
  ...rest
}) {
  const { code, value, relatedFields } = error;

  if (code === 'invalid_object_id') {
    return (
      <ErrorCell>
        <span sx={{ fontWeight: 'bold' }}>{value}</span>{' '}
        <Trans>is not a valid id</Trans>
      </ErrorCell>
    );
  }
  if (code === 'duplicate') {
    return (
      <ErrorCell>
        <span sx={{ fontWeight: 'bold' }}>{value}</span>{' '}
        <Trans>is duplicated</Trans>
      </ErrorCell>
    );
  }
  if (code === 'required') {
    return (
      <ErrorCell>
        <Trans>Value is required</Trans>
      </ErrorCell>
    );
  }
  if (code === 'not_found') {
    return (
      <ErrorCell>
        <span sx={{ fontWeight: 'bold' }}>{value}</span>{' '}
        <Trans>was not found</Trans>
      </ErrorCell>
    );
  }

  if (code === 'invalid_code') {
    return (
      <ErrorCell>
        <span sx={{ fontWeight: 'bold' }}>{value}</span>{' '}
        <Trans>is not a valid code</Trans>
      </ErrorCell>
    );
  }
  if (code === 'product_not_found') {
    return (
      <ErrorCell>
        <Trans>
          Product with ID <span sx={{ fontWeight: 'bold' }}>{value}</span> not
          found in Portfolio
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'question_not_found') {
    return (
      <ErrorCell>
        <Trans>
          The question was not found in Bottlebooks.{' '}
          <span sx={{ fontWeight: 'bold' }}>{value}</span> cannot be imported.
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'internalIdsNotMigrated') {
    return (
      <ErrorCell>
        <Trans>
          System error: Did you forget to run migration?{' '}
          <span sx={{ fontWeight: 'bold' }}>{value}</span> cannot be imported.
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'notUniqueExternalIds') {
    return (
      <ErrorCell>
        <Trans>
          Workspace contains duplicate product external IDs"
          <span sx={{ fontWeight: 'bold' }}>{value}</span>".
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'producer_not_found') {
    return (
      <ErrorCell>
        <Trans>
          Producer with ID <span sx={{ fontWeight: 'bold' }}>{value}</span> not
          found in Bottlebooks
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'invalid_object_id') {
    return (
      <ErrorCell>
        <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid
        Bottlebooks ID
      </ErrorCell>
    );
  }
  if (code === 'InvalidParentCode') {
    return (
      <ErrorCell>
        <Trans>
          Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid
          for related field
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'missingParentCode') {
    return (
      <ErrorCell>
        <Trans>
          <span sx={{ textTransform: 'capitalize' }}>
            {relatedFields[0].bottlebooksFieldName}
          </span>{' '}
          must be defined for{' '}
          <span sx={{ textTransform: 'lowercase' }}>{columnLabel}</span>{' '}
          <span sx={{ fontWeight: 'bold' }}>{value}</span>.
        </Trans>
      </ErrorCell>
    );
  }
  if (code === 'NumberValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid
        number
      </ErrorCell>
    );
  }
  if (code === 'UrlValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid url
      </ErrorCell>
    );
  }
  if (code === 'GLNValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid GLN
        code
      </ErrorCell>
    );
  }
  if (code === 'EanValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid EAN
        code
      </ErrorCell>
    );
  }
  if (code === 'ParentFieldNotSet') {
    const fieldName =
      bottlebooksFieldsMap[`product.${relatedFields[0].bottlebooksFieldName}`]
        ?.importFieldName || relatedFields[0].bottlebooksFieldName;
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be set
        because there is no value set for{' '}
        <span sx={{ fontWeight: 'bold' }}>{fieldName}</span>
      </ErrorCell>
    );
  }
  if (code === 'ParentFieldNotValid') {
    const fieldName =
      bottlebooksFieldsMap[`product.${relatedFields[0].bottlebooksFieldName}`]
        ?.importFieldName || relatedFields[0].bottlebooksFieldName;
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because the value{' '}
        <span sx={{ fontWeight: 'bold' }}>{relatedFields[0].value}</span> set
        for <span sx={{ fontWeight: 'bold' }}>{fieldName}</span> is not valid.
      </ErrorCell>
    );
  }
  if (code === 'ValueSmallerThanParentError') {
    const fieldName =
      bottlebooksFieldsMap[`product.${relatedFields[0].bottlebooksFieldName}`]
        ?.importFieldName || relatedFields[0].bottlebooksFieldName;
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because it cannot be bigger than{' '}
        <span sx={{ fontWeight: 'bold' }}>{relatedFields[0].value}</span> set
        for <span sx={{ fontWeight: 'bold' }}>{fieldName}</span>.
      </ErrorCell>
    );
  }
  if (code === 'ValueBiggerThanParentError') {
    const fieldName =
      bottlebooksFieldsMap[`product.${relatedFields[0].bottlebooksFieldName}`]
        ?.importFieldName || relatedFields[0].bottlebooksFieldName;
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because it cannot be smaller than{' '}
        <span sx={{ fontWeight: 'bold' }}>{relatedFields[0].value}</span> set
        for <span sx={{ fontWeight: 'bold' }}>{fieldName}</span>.
      </ErrorCell>
    );
  }
  if (code === 'IntegerValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because it must be an integer.
      </ErrorCell>
    );
  }
  if (code === 'FloatOneValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because it must be rounded to the nearest 0.1.
      </ErrorCell>
    );
  }
  if (code === 'FloatTwoValidationError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        because it must be rounded to the nearest 0.01.
      </ErrorCell>
    );
  }
  if (code === 'FininAgentError') {
    return <FiningAgentError error={error} />;
  }
  if (code === 'PhoneNumberError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> is not valid
        phone number
      </ErrorCell>
    );
  }
  if (code === 'CalculatePercentageDifferenceError') {
    return (
      <ErrorCell>
        Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be saved
        the difference between the calculated value and the entered value
        exceeds 10%.
      </ErrorCell>
    );
  }
  return (
    <ErrorCell>
      <Trans>Unspecified error ({code})</Trans>
    </ErrorCell>
  );
}

function ErrorCell({ children, ...rest }) {
  return <Text variant="smallest">{children}</Text>;
}

function FiningAgentError({ error }) {
  const { value, relatedFields } = error;
  return (
    <ErrorCell>
      Value <span sx={{ fontWeight: 'bold' }}>{value}</span> can't be set
      because it{' '}
      {relatedFields.length === 2 ? (
        <span>conflicts with Vegan and Vegetarian.</span>
      ) : (
        <span>
          conflicts with{' '}
          {relatedFields[0].bottlebooksFieldName === 'isVegan'
            ? 'Vegan'
            : 'Vegetarian'}
        </span>
      )}
    </ErrorCell>
  );
}
